/**
 * One person's own order for the board — its groups, the people in them, and the fleet.
 *
 * It lives in the browser for the session and is never sent anywhere: it is how one
 * scheduler likes to read the board, not a fact about the company, so it changes nothing
 * for anybody else, for the wall, or for the schedule email.
 *
 * Kept as ranked lists of ids rather than as positions, so it adapts on its own. Somebody
 * added since a list was saved is not in it and is drawn after everyone who is, in the
 * board's own order; somebody deleted is in it and names nothing, so is skipped. Neither
 * needs the saved list to be touched, and a list nobody has touched is not stored at all,
 * so the board goes on following the company's order — new people in their proper place
 * included. The same goes for a new group, and a new machine.
 *
 *   bands.normal / bands.cat    band keys — the order of the PMs, or of the groups
 *   groups.normal / groups.cat  { [band key]: ids } — the people within each band
 *   people                      ids — the one list on a Group board grouped by person
 *   assets                      ids — the fleet's machines
 *   assetBands                  asset group ids — the order of the fleet's bands
 *
 * People are kept as a list PER BAND, not one ranking of everybody, because one person can
 * be listed in two bands in two different orders: on two PMs' teams, each kept in the order
 * that PM added people, or under their own group and under "Not on a team" when booked with
 * no group. A single ranking cannot hold both, so it showed the second band out of the order
 * the board drew it in, and saving an edit anywhere pushed that onto the board. A machine is
 * in at most one band (migrations/014), so the fleet's one list is enough.
 *
 * The Team board's lists are their own because its bands are PMs, not groups. The fleet's
 * are shared by every arrangement, because it is the same rows in each.
 *
 * Kept out of Board.jsx because that page reads useAuth and is not rendered in the tests.
 */

export const EMPTY_ORDER = Object.freeze({
  bands: Object.freeze({}),
  groups: Object.freeze({}),
  people: null,
  assets: null,
  assetBands: null,
})

/** The key a band answers to — the grids' own groupKey. The pseudo-band is 'unassigned'. */
export const bandKey = (group) => group?.id ?? 'unassigned'

/**
 * The bands that never move: "Not on a team" / "Unassigned staff", and the fleet's band of
 * machines in no group. Both are the absence of a band rather than one, and the grids lean on
 * the first being last — BoardGrid works out somebody's crew by which band lists them last,
 * and somebody with a crew who is also booked with none is listed in both.
 */
export const PINNED_BAND = 'unassigned'
export const PINNED_ASSET_BAND = '__ungrouped__' // FleetBand's UNGROUPED

const idList = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : null)
const present = (list) => (list?.length ? list : null)

const bandLists = (v) => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const out = {}
  for (const [key, ids] of Object.entries(v)) {
    const list = idList(ids)
    if (list?.length) out[key] = list
  }
  return Object.keys(out).length ? out : null
}

/**
 * A stored order, checked on the way in: it came out of storage that a different version of
 * this page may have written, or that somebody edited by hand. Anything not in the shape is
 * dropped rather than trusted — including a pinned band named in a band order.
 */
export function readOrder(raw) {
  if (!raw || typeof raw !== 'object') return EMPTY_ORDER
  const bands = {}
  const groups = {}
  for (const mode of ['normal', 'cat']) {
    const order = present(idList(raw.bands?.[mode])?.filter((k) => k !== PINNED_BAND))
    if (order) bands[mode] = order
    const lists = bandLists(raw.groups?.[mode])
    if (lists) groups[mode] = lists
  }
  return {
    bands,
    groups,
    people: present(idList(raw.people)),
    assets: present(idList(raw.assets)),
    assetBands: present(idList(raw.assetBands)?.filter((k) => k !== PINNED_ASSET_BAND)),
  }
}

/**
 * What the board is drawn by, for how it is arranged right now: the one people list when it
 * is grouped by person, otherwise this mode's band order and its list per band. The fleet's
 * either way.
 */
export function orderFor(order, mode, byPeople) {
  return {
    staff: byPeople ? order?.people || null : null,
    bandOrder: byPeople ? null : order?.bands?.[mode] || null,
    bands: byPeople ? null : order?.groups?.[mode] || null,
    assets: order?.assets || null,
    assetBands: order?.assetBands || null,
  }
}

/**
 * `items` in the order `ids` gives: the ones it names first, in its order, then the ones it
 * does not, in the order they arrived. An id that names nothing here is skipped. Returns the
 * same objects, and `items` itself when there is no order to apply.
 */
export function sortByIds(items, ids, idOf = (x) => x) {
  if (!items || !ids?.length) return items
  const rank = new Map(ids.map((id, i) => [id, i]))
  return items
    .map((item, i) => ({ item, i, r: rank.has(idOf(item)) ? rank.get(idOf(item)) : Infinity }))
    .sort((a, b) => (a.r === b.r ? a.i - b.i : a.r < b.r ? -1 : 1))
    .map((x) => x.item)
}

/** Bands in the order given, with the pinned band kept last whatever the order says. */
export function sortBands(items, ids, keyOf, pinned) {
  const sorted = sortByIds(items, ids, keyOf)
  const last = sorted.filter((b) => keyOf(b) === pinned)
  return last.length ? [...sorted.filter((b) => keyOf(b) !== pinned), ...last] : sorted
}

/**
 * The board in this order.
 *
 * Only ever a permutation: every list keeps exactly the ids it had, and every band keeps its
 * id and kind; the pinned band stays last. `staff` orders the board's people — which is what
 * the board grouped by person builds its one band from. `bands` orders each band's people by
 * its own list and leaves a band with none alone; `bandOrder` orders the bands.
 *
 * The fleet's keys are left absent when the board has none: their absence is what tells the
 * page the company does not draw the fleet, and an empty list would draw an empty band.
 */
export function applyBoardOrder(board, { staff, bands, bandOrder, assets, assetBands } = {}) {
  if (!board) return board
  const people = Boolean(staff?.length)
  const within = Boolean(bands && Object.keys(bands).length)
  const across = Boolean(bandOrder?.length)
  const fleet = Boolean(assets?.length && board.assets)
  const fleetBands = Boolean(assetBands?.length && board.assetGroups)
  if (!people && !within && !across && !fleet && !fleetBands) return board

  let groups = board.groups || []
  if (within) {
    groups = groups.map((g) => {
      const list = bands[bandKey(g)]
      return list?.length ? { ...g, staffIds: sortByIds(g.staffIds || [], list) } : g
    })
  }
  if (across) groups = sortBands(groups, bandOrder, bandKey, PINNED_BAND)

  return {
    ...board,
    ...(people && { staff: sortByIds(board.staff || [], staff, (s) => s.id) }),
    ...((within || across) && { groups }),
    ...(fleet && { assets: sortByIds(board.assets, assets, (a) => a.id) }),
    // The fleet's no-group band is not in assetGroups at all — FleetBand adds it last — so
    // there is nothing here to pin.
    ...(fleetBands && { assetGroups: sortByIds(board.assetGroups, assetBands, (g) => g.id) }),
  }
}

// ── The settings dialog's lists ─────────────────────────────────────────────────────────

const uniq = (ids) => [...new Set(ids)]

/**
 * The board's bands as the dialog lists them in Groups view: each with the people drawn
 * under it, in the board's own order for that band — which is also that band's default —
 * and what the dialog needs to draw its header the way the board does. Only people the board
 * has a record for (a band can name somebody since disabled), and no band with nobody in it.
 */
export function groupSections(board) {
  const known = new Set((board?.staff || []).map((s) => s.id))
  return (board?.groups || [])
    .map((g) => ({
      key: bandKey(g),
      name: g.name,
      kind: g.kind,
      color: g.color || null,
      subtitle: g.kind !== 'cat' ? g.subtitle || null : null,
      pinned: bandKey(g) === PINNED_BAND,
      ids: uniq((g.staffIds || []).filter((id) => known.has(id))),
    }))
    .filter((s) => s.ids.length > 0)
}

/** Everybody, as the board grouped by person draws them. */
export const defaultPeopleOrder = (board) => (board?.staff || []).map((s) => s.id)

/** The fleet, in the register's order. */
export const defaultAssetOrder = (board) => (board?.assets || []).map((a) => a.id)

/** Move `id` to just before `target` in `list`, or just after it. A new list. */
export function moveId(list, id, target, after = false) {
  if (id === target) return list
  const out = list.filter((x) => x !== id)
  const at = out.indexOf(target)
  if (at === -1) return list
  out.splice(after ? at + 1 : at, 0, id)
  return out
}

export const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i])

/**
 * What to keep for a list once it has been edited.
 *
 * `null` when it is the board's own order again, so the board follows the company's order
 * from then on rather than a frozen copy of it — which would put everybody added later at
 * the bottom instead of in their place.
 *
 * Otherwise the edit, plus anything the previous list placed that is not on this board right
 * now — a machine out of service this week, somebody this person cannot see today, a group
 * with nobody in it — each put back just after whatever came before it last time, so saving
 * an unrelated change does not cost it its place.
 */
export function toStored(edited, defaults, previous) {
  if (sameList(edited, defaults)) return null
  const out = [...edited]
  const placed = new Set(out)
  let anchor = null
  for (const id of previous || []) {
    if (placed.has(id)) {
      anchor = id
      continue
    }
    out.splice(anchor === null ? 0 : out.indexOf(anchor) + 1, 0, id)
    placed.add(id)
    anchor = id
  }
  return out
}
